import { useMemo } from 'react';

import { UploadedFile } from '@components/file-field';
import { FormLabel } from '@components/form-label';
import { usePreviewFileModal } from '@components/modals/preview-file-modal';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { Color } from '@styles/theme';
import { abbreviateDate } from '@utils/date';
import { formatNumber, hasNumericValue } from '@utils/numbers';

import { FormAnswerFieldType, FormAnswersV2, FormFile } from 'api/journey-action-forms/requests';

export const ResponseContentV2 = ({ formAnswers }: { formAnswers: FormAnswersV2 }) => {
  const formFiles = useMemo(() => {
    let formFiles: FormFile[] = [];

    formAnswers.forEach((field) => {
      if (field.kind === 'input-file' && field.value) {
        formFiles = formFiles.concat(field.value);
      }
    });
    return formFiles;
  }, [formAnswers]);

  return (
    <Stack space={6}>
      {formAnswers.map((field) => (
        <RenderFieldContent key={field.id} field={field} files={formFiles} />
      ))}
    </Stack>
  );
};

const RenderFieldContent = ({
  field,
  files,
}: {
  field: FormAnswerFieldType;
  files: Array<FormFile>;
}) => {
  const showFilePreviewModal = usePreviewFileModal();
  const { t } = useScopedTranslate(locale);

  const fieldContent = (field: FormAnswerFieldType) => {
    const hasAnswer =
      'value' in field &&
      (typeof field.value === 'number' ||
        (typeof field.value === 'string' && field.value.trim().length > 0) ||
        (Array.isArray(field.value) && field.value.length > 0));

    const color: Color = hasAnswer ? 'textPrimary' : 'textHelper';

    switch (field.kind) {
      case 'input-file':
        return (
          <Stack space={2}>
            {field.value?.map((file, index) => (
              <UploadedFile
                key={index}
                name={file.name}
                path={file.src}
                onPreview={() => {
                  showFilePreviewModal({ formFile: file, filesList: files });
                }}
              />
            ))}
            {!field.value && <Text font="regular14" color={color} text={t('noAnswer')} />}
          </Stack>
        );
      case 'separator':
        return <Separator />;
      case 'heading':
        return <Text font="heading2" text={field.label} />;
      case 'subheading':
        return <Text font="bold18" text={field.label} />;
      case 'input-text-small':
      case 'input-text-large':
      case 'input-phone':
      case 'input-kennitala':
        return <Text font="regular14" color={color} text={field.value || t('noAnswer')} />;
      case 'input-number':
        const formattedNumber =
          field.value !== null && hasNumericValue(field.value)
            ? formatNumber(field.value)
            : undefined;
        return (
          <Text
            font="regular14"
            color={color}
            text={hasAnswer ? formattedNumber ?? t('noAnswer') : t('noAnswer')}
          />
        );
      case 'input-email':
        return <Text font="regular14" color={color} text={field.value || t('noAnswer')} />;
      case 'input-single-choice':
        return (
          <Text
            font="regular14"
            color={color}
            text={field.options.find((opt) => opt.value === field.value)?.label || t('noAnswer')}
          />
        );
      case 'input-multiple-choice':
        return (
          <Text
            font="regular14"
            color={color}
            text={
              field.value?.length
                ? field.options
                    .filter((opt) => field.value.some((val) => val === opt.value))
                    .map((field) => field.label)
                    .join(', ')
                : t('noAnswer')
            }
          />
        );
      case 'input-date':
        return (
          <Text
            font="regular14"
            color={color}
            text={field.value ? abbreviateDate(field.value, 'en', 'datetime') : t('noAnswer')}
          />
        );
      case 'input-dropdown':
        return (
          <Text
            font="regular14"
            color={color}
            text={field.options.find((opt) => opt.value === field.value)?.label || t('noAnswer')}
          />
        );
    }

    return null;
  };

  const content = fieldContent(field);

  if (field.kind !== 'heading' && field.kind !== 'subheading' && 'label' in field) {
    return (
      <div>
        <FormLabel text={field.label} />
        {content}
      </div>
    );
  }
  return content;
};

const locale = {
  en: {
    noAnswer: 'No answer provided',
  },
};
